import { Fragment, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { AlertCircle, ArrowDownWideNarrow, BookOpen, Building2, CheckCircle2, Download, RefreshCw, Search } from 'lucide-react'
import { toast } from 'sonner'
import { Alert, AlertDescription, AlertTitle } from '@quebon/ui/components/alert'
import { Badge } from '@quebon/ui/components/badge'
import { Button } from '@quebon/ui/components/button'
import { Card, CardAction, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@quebon/ui/components/card'
import { Checkbox } from '@quebon/ui/components/checkbox'
import { Field, FieldGroup, FieldLabel } from '@quebon/ui/components/field'
import { Input } from '@quebon/ui/components/input'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@quebon/ui/components/select'
import { Spinner } from '@quebon/ui/components/spinner'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@quebon/ui/components/table'
import { saveBlob } from '@/api/csvDownload'
import { learnerProgressApi, type LearnerProgressParams, type LearnerProgressRow } from '@/api/textbookProgressBoard'

const INITIAL_PARAMS: LearnerProgressParams = { includeTest: false, q: '', page: 0, size: 20, sort: 'PROGRESS' }

function gradeLabel(grade: string | null): string {
  if (!grade) return '—'
  if (/^elementary[1-6]$/.test(grade)) return `초${grade.slice(-1)}`
  if (/^middle[1-3]$/.test(grade)) return `중${grade.slice(-1)}`
  return ({ preschool: '미취학', high: '고등학생', university: '대학생', adult: '성인' } as Record<string, string>)[grade] ?? grade
}

function formatKstDateTime(value: string): string {
  return new Date(value).toLocaleString('ko-KR', {
    timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  })
}

export function LearnerProgressCells({ row }: { row: LearnerProgressRow }) {
  return (
    <>
      <TableCell>
        <div className="flex items-center gap-2 text-muted-foreground">
          <Building2 className="size-4 shrink-0" aria-hidden="true" />
          <span>{row.organizationName}</span>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex flex-col gap-1">
          <span className="font-semibold">{row.fullName}</span>
          {row.testCenter && <Badge variant="outline">테스트 센터</Badge>}
          {row.testEntitlement && <Badge variant="outline">테스트 수강권</Badge>}
        </div>
      </TableCell>
      <TableCell><span className="text-muted-foreground">{gradeLabel(row.gradeLevel)}</span></TableCell>
      <TableCell><span className="text-muted-foreground">{row.gender === 'male' ? '남' : row.gender === 'female' ? '여' : '—'}</span></TableCell>
      <TableCell><Badge variant="outline">{row.productName ?? <ContentMetadataText text={row.curriculumTitle ?? '—'} />}</Badge></TableCell>
      <TableCell>
        {row.progressStatus === 'COMPLETED' ? <Badge variant="secondary"><CheckCircle2 data-icon="inline-start" />과정 완료</Badge>
          : row.progressStatus === 'UNAVAILABLE' ? <Badge variant="destructive"><AlertCircle data-icon="inline-start" />진도 확인 필요</Badge>
          : (
            <div className="flex flex-col gap-2 py-1">
              <div className="flex flex-wrap items-center gap-2" data-course={row.courseColorKey ?? 'unknown'}>
                <Badge variant="category"><ContentMetadataText text={row.courseName} /></Badge>
                <Badge variant="category"><span className="tabular-nums">{row.chapterNumber}챕터 · {row.lessonNumber}{row.chapterLessonCount ? `/${row.chapterLessonCount}` : ''}레슨</span></Badge>
              </div>
              <span className="text-xs text-muted-foreground"><ContentMetadataText text={row.chapterTitle} /> / <ContentMetadataText text={row.lessonTitle} /></span>
            </div>
          )}
      </TableCell>
      <TableCell className="whitespace-nowrap"><span className="text-xs text-muted-foreground">{row.lastAccessedAt ? formatKstDateTime(row.lastAccessedAt) : '학습 기록 없음'}</span></TableCell>
    </>
  )
}

export function LearnerProgressPage() {
  const [params, setParams] = useState<LearnerProgressParams>(INITIAL_PARAMS)
  const [draftSearch, setDraftSearch] = useState('')
  const [downloading, setDownloading] = useState(false)
  const query = useQuery({
    queryKey: ['admin', 'textbook-progress-board', 'learners', params],
    queryFn: () => learnerProgressApi.list(params),
    placeholderData: previous => previous,
  })
  const data = query.data
  const totalPages = Math.max(1, Math.ceil((data?.totalElements ?? 0) / params.size))
  const updateFilters = (changes: Partial<LearnerProgressParams>) => {
    setParams(previous => ({ ...previous, ...changes, page: 0 }))
  }
  const download = async () => {
    setDownloading(true)
    try {
      const { blob, filename } = await learnerProgressApi.exportCsv(params)
      saveBlob(blob, filename)
    } catch {
      toast.error('CSV 다운로드에 실패했습니다.')
    } finally {
      setDownloading(false)
    }
  }
  return (
    <Card>
      <CardHeader className="border-b">
        <CardTitle>전체 원생 진도 현황</CardTitle>
        <CardDescription>
          정규 과정 재원 원생의 현재 진도를 비교합니다. 휴원·무료체험은 제외하며, 진도는 현재 수강의 첫 번째 미완료 레슨입니다.
        </CardDescription>
        <CardAction>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => query.refetch()} disabled={query.isFetching}>
              <RefreshCw data-icon="inline-start" />새로고침
            </Button>
            <Button onClick={download} disabled={downloading || query.isFetching || query.isError || !data?.totalElements}>
              {downloading ? <Spinner data-icon="inline-start" /> : <Download data-icon="inline-start" />}CSV 다운로드
            </Button>
          </div>
        </CardAction>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <form className="rounded-lg border bg-muted/30 p-4" onSubmit={event => { event.preventDefault(); updateFilters({ q: draftSearch.trim() }) }}>
          <FieldGroup className="flex flex-wrap items-end gap-3 sm:flex-row">
            <Field className="w-full sm:w-52">
              <FieldLabel htmlFor="learner-progress-center">센터</FieldLabel>
              <Select value={params.organizationId?.toString() ?? 'all'} onValueChange={value => updateFilters({ organizationId: value && value !== 'all' ? Number(value) : undefined })}>
                <SelectTrigger id="learner-progress-center" className="w-full"><SelectValue>{params.organizationId ? data?.organizations.find(option => option.id === params.organizationId)?.name : '전체 센터'}</SelectValue></SelectTrigger>
                <SelectContent><SelectGroup>
                  <SelectItem value="all">전체 센터</SelectItem>
                  {data?.organizations.map(option => <SelectItem key={option.id} value={option.id.toString()}>{option.name}</SelectItem>)}
                </SelectGroup></SelectContent>
              </Select>
            </Field>
            <Field className="w-full sm:w-44">
              <FieldLabel htmlFor="learner-progress-sort">정렬</FieldLabel>
              <Select value={params.sort} onValueChange={value => {
                if (value === 'PROGRESS' || value === 'CENTER_NAME') updateFilters({ sort: value })
              }}>
                <SelectTrigger id="learner-progress-sort" className="w-full">
                  <SelectValue>{params.sort === 'CENTER_NAME' ? '센터·이름순' : params.curriculumId ? '과정 내 진도순' : '코스별 진도순'}</SelectValue>
                </SelectTrigger>
                <SelectContent><SelectGroup>
                  <SelectItem value="PROGRESS">{params.curriculumId ? '과정 내 진도순' : '코스별 진도순'}</SelectItem>
                  <SelectItem value="CENTER_NAME">센터·이름순</SelectItem>
                </SelectGroup></SelectContent>
              </Select>
            </Field>
            <Field className="w-full sm:w-44">
              <FieldLabel htmlFor="learner-progress-curriculum">과정</FieldLabel>
              <Select value={params.curriculumId?.toString() ?? 'all'} onValueChange={value => updateFilters({ curriculumId: value && value !== 'all' ? Number(value) : undefined })}>
                <SelectTrigger id="learner-progress-curriculum" className="w-full"><SelectValue>{params.curriculumId ? data?.curricula.find(option => option.id === params.curriculumId)?.name : '전체 과정'}</SelectValue></SelectTrigger>
                <SelectContent><SelectGroup>
                  <SelectItem value="all">전체 과정</SelectItem>
                  {data?.curricula.map(option => <SelectItem key={option.id} value={option.id.toString()}>{option.name}</SelectItem>)}
                </SelectGroup></SelectContent>
              </Select>
            </Field>
            <Field className="w-full sm:w-52">
              <FieldLabel htmlFor="learner-progress-search">원생 이름</FieldLabel>
              <Input id="learner-progress-search" value={draftSearch} maxLength={100} onChange={event => setDraftSearch(event.target.value)} placeholder="이름으로 검색" />
            </Field>
            <Button type="submit" variant="secondary"><Search data-icon="inline-start" />검색</Button>
            <Button type="button" variant="outline" onClick={() => { setDraftSearch(''); setParams(INITIAL_PARAMS) }}>초기화</Button>
            <Field orientation="horizontal" className="w-auto">
              <Checkbox id="learner-progress-test" checked={params.includeTest} onCheckedChange={checked => updateFilters({ includeTest: checked === true, organizationId: undefined })} />
              <FieldLabel htmlFor="learner-progress-test">테스트 센터 포함</FieldLabel>
            </Field>
          </FieldGroup>
        </form>
        {query.isError && <Alert variant="destructive"><AlertTitle>불러오기 실패</AlertTitle><AlertDescription>전체 원생 진도를 불러오지 못했습니다. 새로고침으로 다시 시도해 주세요.</AlertDescription></Alert>}
        {query.isPending && <div className="flex justify-center py-12" role="status" aria-label="진도 불러오는 중"><Spinner /></div>}
        {data && !query.isError && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3" role="status" aria-live="polite">
              <div className="flex items-center gap-3">
                <span className="font-semibold">조회 {data.totalElements}명</span>
                <span className="text-xs text-muted-foreground">전체 대상 {data.eligibleLearnerCount}명</span>
                {query.isFetching && <span className="text-xs text-muted-foreground">갱신 중…</span>}
              </div>
              <span className="text-xs text-muted-foreground">집계 {formatKstDateTime(data.generatedAt)}</span>
            </div>
            {params.sort === 'PROGRESS' && (
              <div className="flex items-start gap-2 text-xs text-muted-foreground">
                <ArrowDownWideNarrow className="size-4 shrink-0" aria-hidden="true" />
                <span>{params.curriculumId
                  ? '선택 과정의 전체 레슨 순서에서 앞선 진도부터 표시합니다.'
                  : 'Core A → B → C → D → E → Advanced → Elite 순으로 묶고, 같은 코스 안에서 앞선 진도부터 표시합니다. 분할 코스는 이어서 비교합니다.'} 완료자는 하단에 표시합니다.</span>
              </div>
            )}
            <Table aria-label="전체 원생 진도 현황" aria-busy={query.isFetching}>
              <TableHeader><TableRow>{['센터명', '이름', '학년', '성별', '과정', '진도', '마지막 학습일'].map(label => <TableHead key={label}>{label}</TableHead>)}</TableRow></TableHeader>
              <TableBody>
                {data.rows.length ? data.rows.map((row, index) => (
                  <Fragment key={`${row.organizationId}-${row.userId}`}>
                    {data.sort === 'PROGRESS' && (index === 0 || data.rows[index - 1].progressGroupKey !== row.progressGroupKey) && (
                      <TableRow><TableCell colSpan={7}>
                        <div className="flex items-center gap-2 rounded-md bg-muted px-3 py-2">
                          <BookOpen className="size-4 text-primary" aria-hidden="true" />
                          <span className="font-semibold"><ContentMetadataText text={row.progressGroupName} /></span>
                          {row.progressStatus === 'CURRENT' && <span className="text-xs text-muted-foreground">진도 내림차순</span>}
                        </div>
                      </TableCell></TableRow>
                    )}
                    <TableRow><LearnerProgressCells row={row} /></TableRow>
                  </Fragment>
                ))
                  : <TableRow><TableCell colSpan={7}>조건에 맞는 원생이 없습니다. 검색·필터 조건을 확인해 주세요.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </>
        )}
      </CardContent>
      <CardFooter className="flex flex-wrap justify-between gap-3 border-t">
        <span className="text-muted-foreground">CSV는 현재 검색·필터에 맞는 전체 원생을 포함합니다.</span>
        <div className="flex items-center gap-3" aria-label="원생 목록 페이지">
          <Button variant="outline" disabled={params.page === 0 || query.isFetching} onClick={() => setParams(previous => ({ ...previous, page: previous.page - 1 }))}>이전</Button>
          <span className="tabular-nums">{params.page + 1} / {totalPages}</span>
          <Button variant="outline" disabled={!data || query.isError || params.page + 1 >= totalPages || query.isFetching} onClick={() => setParams(previous => ({ ...previous, page: previous.page + 1 }))}>다음</Button>
        </div>
      </CardFooter>
    </Card>
  )
}
